import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bottles,
  CellarEvents,
  MarketValue,
  Pricing,
  type Bottle,
  type CatalogMarketValue,
  type CellarEvent,
  type CellarEventActivity,
} from '@/lib/api';
import { Button, Drawer, Icon, Input, SwitchRow, Tabs, Badge, Spinner, IconButton } from '@/design-system';
import { formatPriceNok, formatPercent, formatDate } from '@/lib/format';
import { useToasts } from '@/lib/store';
import { LocationPicker } from '@/components/LocationPicker';
import { SellBottleModal } from '@/components/SellBottleModal';

type Tab = 'pricing' | 'quantity' | 'history' | 'details';

/**
 * Standard Norwegian VAT rate on wine purchases (25%) — mirrors
 * backend/src/services/pricing-service.ts's `deriveVatPricePair`, which is
 * the source of truth applied on save. Kept here too so the Prising tab's
 * two purchase-price fields visibly auto-fill each other as the owner
 * types, instead of only resolving after the next fetch.
 */
const WINE_VAT_MULTIPLIER = 1.25;

function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

interface BottlePricingDrawerProps {
  open: boolean;
  onClose: () => void;
  bottleId?: string;
  /** Opens directly on a given tab instead of always starting at "Prising". */
  initialTab?: Tab;
}

interface PriceDraft {
  purchasePriceExVatNok?: number;
  purchasePriceNok?: number;
  sellingPriceNok?: number;
  lockSellingPrice: boolean;
  glassPriceNok?: number;
  glassSizeMl?: number;
  availableByGlass: boolean;
  supplier?: string;
}

function draftFromBottle(bottle: Bottle): PriceDraft {
  return {
    purchasePriceExVatNok: bottle.purchasePriceExVatNok,
    purchasePriceNok: bottle.purchasePriceNok,
    sellingPriceNok: bottle.sellingPriceNok,
    lockSellingPrice: bottle.lockSellingPrice,
    glassPriceNok: bottle.glassPriceNok,
    glassSizeMl: bottle.glassSizeMl,
    availableByGlass: bottle.availableByGlass,
    supplier: bottle.supplier,
  };
}

/**
 * Slide-in pricing panel — the per-bottle half of the "Prisautomat". Tabs
 * mirror the Vinify inspiration: Prising is the full editor; the other three
 * are lighter read-only views wired to data that already exists elsewhere
 * (racks, tasting notes, catalog match).
 */
export function BottlePricingDrawer({ open, onClose, bottleId, initialTab }: BottlePricingDrawerProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [tab, setTab] = useState<Tab>(initialTab ?? 'pricing');
  const [draft, setDraft] = useState<PriceDraft | null>(null);

  const bottleQ = useQuery({
    queryKey: ['bottle', bottleId],
    queryFn: () => Bottles.get(bottleId!),
    enabled: open && Boolean(bottleId),
  });

  const overviewQ = useQuery({
    queryKey: ['pricing.overview'],
    queryFn: () => Pricing.overview(),
    enabled: open,
    staleTime: 15_000,
  });

  const notesQ = useQuery({
    queryKey: ['bottle.notes', bottleId],
    queryFn: () => Bottles.notes(bottleId!),
    enabled: open && Boolean(bottleId) && tab === 'history',
  });

  const eventsQ = useQuery({
    queryKey: ['bottle.events', bottleId],
    queryFn: () => CellarEvents.list({ bottleId: bottleId!, limit: 50 }),
    enabled: open && Boolean(bottleId) && tab === 'history',
  });

  const catalogId = bottleQ.data?.catalogId;
  const marketValueQ = useQuery({
    queryKey: ['market-value', catalogId],
    queryFn: () => MarketValue.forCatalog(catalogId!),
    enabled: open && tab === 'pricing' && Boolean(catalogId),
    staleTime: 60_000,
  });

  const [sellOpen, setSellOpen] = useState(false);

  useEffect(() => {
    if (open) setTab(initialTab ?? 'pricing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bottleId, initialTab]);

  useEffect(() => {
    if (bottleQ.data) setDraft(draftFromBottle(bottleQ.data));
  }, [bottleQ.data]);

  const overviewRow = useMemo(
    () => overviewQ.data?.bottles.find((r) => r.bottle.id === bottleId),
    [overviewQ.data, bottleId],
  );

  const save = useMutation({
    mutationFn: () => {
      if (!bottleQ.data || !draft) throw new Error('Ingen flaske lastet');
      return Bottles.update(bottleQ.data.id, draft, bottleQ.data.etag);
    },
    onSuccess: (updated) => {
      push({ title: t('bottle.saved'), description: updated.name, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['bottle', bottleId] });
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      void qc.invalidateQueries({ queryKey: ['pricing.overview'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const bottle = bottleQ.data;
  const loading = bottleQ.isLoading || !draft;

  return (
    <>
    <Drawer open={open} onClose={onClose} title={bottle?.name ?? t('pricing.title')} width="480px" footer={
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <div className="flex items-center gap-2">
          {bottle && bottle.qty > 0 && (
            <Button variant="ghost" iconLeft="external" onClick={() => setSellOpen(true)}>
              {t('pricing.sell.action')}
            </Button>
          )}
          <Button variant="primary" iconLeft="check" loading={save.isPending} disabled={loading} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    }>
      <div className="grid gap-4">
        <Tabs<Tab>
          tabs={[
            { id: 'pricing', label: t('pricing.tabPricing'), icon: 'receipt' },
            { id: 'quantity', label: t('pricing.tabQuantity'), icon: 'rack' },
            { id: 'history', label: t('pricing.tabHistory'), icon: 'clock' },
            { id: 'details', label: t('pricing.tabDetails'), icon: 'info' },
          ]}
          active={tab}
          onChange={setTab}
        />

        {loading ? (
          <div className="grid place-items-center py-12"><Spinner /></div>
        ) : tab === 'pricing' && bottle && draft ? (
          <PricingTab
            draft={draft}
            setDraft={(updater) => setDraft((prev) => (prev ? updater(prev) : prev))}
            catalogPriceNok={overviewRow?.catalogPriceNok}
            catalogPriceUpdatedAt={overviewRow?.catalogPriceUpdatedAt}
            suggestedPriceNok={overviewRow?.suggestedPriceNok}
            sellBuyRatioPct={overviewRow?.sellBuyRatioPct}
            costOfGoodsPct={overviewRow?.costOfGoodsPct}
            surchargePct={overviewRow?.surchargePct}
            marketValue={marketValueQ.data}
            marketValueLoading={Boolean(catalogId) && marketValueQ.isLoading}
          />
        ) : tab === 'quantity' && bottle ? (
          <QuantityTab bottle={bottle} />
        ) : tab === 'history' ? (
          <HistoryTab
            events={eventsQ.data?.events}
            eventsLoading={eventsQ.isLoading}
            notes={notesQ.data}
            loading={notesQ.isLoading}
          />
        ) : tab === 'details' && bottle ? (
          <DetailsTab bottle={bottle} />
        ) : null}
      </div>
    </Drawer>
    <SellBottleModal open={sellOpen} onClose={() => setSellOpen(false)} bottle={bottle} />
    </>
  );
}

function PricingTab({
  draft,
  setDraft,
  catalogPriceNok,
  catalogPriceUpdatedAt,
  suggestedPriceNok,
  sellBuyRatioPct,
  costOfGoodsPct,
  surchargePct,
  marketValue,
  marketValueLoading,
}: {
  draft: PriceDraft;
  setDraft: (updater: (d: PriceDraft) => PriceDraft) => void;
  catalogPriceNok?: number;
  catalogPriceUpdatedAt?: string;
  suggestedPriceNok?: number;
  sellBuyRatioPct?: number;
  costOfGoodsPct?: number;
  surchargePct?: number;
  marketValue?: CatalogMarketValue;
  marketValueLoading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-5">
      <section className="grid gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('pricing.tabPricing')} — innkjøp</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label={t('pricing.purchasePriceExVat')}
            type="number"
            min={0}
            step={0.5}
            value={draft.purchasePriceExVatNok ?? ''}
            onChange={(e) => {
              const exVat = e.target.value === '' ? undefined : Number(e.target.value);
              setDraft((d) => ({
                ...d,
                purchasePriceExVatNok: exVat,
                // Auto-fill the inc-VAT twin from this one (25% Norwegian
                // wine VAT) — either field stays independently editable
                // afterwards, this just keeps them in sync as-you-type so
                // both are always saved together (see cellar-repository.ts
                // updateBottle / deriveVatPricePair, the write-time fallback).
                purchasePriceNok: exVat === undefined ? undefined : roundToCents(exVat * WINE_VAT_MULTIPLIER),
              }));
            }}
          />
          <Input
            label={t('pricing.purchasePriceIncVat')}
            type="number"
            min={0}
            step={0.5}
            value={draft.purchasePriceNok ?? ''}
            onChange={(e) => {
              const incVat = e.target.value === '' ? undefined : Number(e.target.value);
              setDraft((d) => ({
                ...d,
                purchasePriceNok: incVat,
                purchasePriceExVatNok: incVat === undefined ? undefined : roundToCents(incVat / WINE_VAT_MULTIPLIER),
              }));
            }}
          />
        </div>
        <Input
          label={t('pricing.supplier')}
          value={draft.supplier ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, supplier: e.target.value || undefined }))}
        />
      </section>

      <div className="ui-divider" />

      <section className="grid gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('pricing.tabPricing')} — salg</h3>
        <Input
          label={t('pricing.sellingPrice')}
          type="number"
          min={0}
          step={5}
          value={draft.sellingPriceNok ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, sellingPriceNok: e.target.value === '' ? undefined : Number(e.target.value) }))}
        />
        <SwitchRow
          label={t('pricing.lockPrice')}
          description={t('pricing.lockPriceHint')}
          checked={draft.lockSellingPrice}
          onChange={(checked) => setDraft((d) => ({ ...d, lockSellingPrice: checked }))}
        />

        <MarketValuePanel marketValue={marketValue} loading={marketValueLoading} fallbackPriceNok={catalogPriceNok} fallbackUpdatedAt={catalogPriceUpdatedAt} />

        <div className="ui-card grid gap-2 p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-mist">{t('pricing.suggestedPrice')}</span>
            <span className="flex items-center gap-2">
              <span className="text-parchment">{suggestedPriceNok != null ? formatPriceNok(suggestedPriceNok) : t('pricing.noBasis')}</span>
              {suggestedPriceNok != null && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDraft((d) => ({ ...d, sellingPriceNok: suggestedPriceNok }))}
                >
                  {t('pricing.applySuggested')}
                </Button>
              )}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <MetricTile label={t('pricing.sellBuyRatio')} value={formatPercent(sellBuyRatioPct)} />
          <MetricTile label={t('pricing.costOfGoods')} value={formatPercent(costOfGoodsPct)} />
        </div>
        <MetricTile label={t('pricing.surcharge')} value={formatPercent(surchargePct)} />
      </section>

      <div className="ui-divider" />

      <section className="grid gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">Glass</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label={t('pricing.glassPrice')}
            type="number"
            min={0}
            step={5}
            value={draft.glassPriceNok ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, glassPriceNok: e.target.value === '' ? undefined : Number(e.target.value) }))}
          />
          <Input
            label={t('pricing.glassSize')}
            type="number"
            min={1}
            step={10}
            value={draft.glassSizeMl ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, glassSizeMl: e.target.value === '' ? undefined : Number(e.target.value) }))}
          />
        </div>
        <SwitchRow
          label={t('pricing.soldByGlass')}
          checked={draft.availableByGlass}
          onChange={(checked) => setDraft((d) => ({ ...d, availableByGlass: checked }))}
        />
      </section>
    </div>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="ui-card grid gap-1 p-3 text-center">
      <span className="text-[10px] uppercase tracking-widest text-mist">{label}</span>
      <span className="font-serif text-lg text-parchment">{value}</span>
    </div>
  );
}

const TREND_ICON: Record<'up' | 'down' | 'flat', { icon: 'trend-up' | 'trend-down' | 'minus'; className: string }> = {
  up: { icon: 'trend-up', className: 'text-success' },
  down: { icon: 'trend-down', className: 'text-danger' },
  flat: { icon: 'minus', className: 'text-mist' },
};

/**
 * Self-owned market value ("no Liv-ex") — current price + trend arrow + a
 * compact inline sparkline off `catalog_price_history`, plus conservative
 * cross-market matches (see CellarRepository.crossMarketMatches). Falls back
 * to the plain pricingOverview join (`fallbackPriceNok`) for bottles whose
 * catalog match has no history yet, so the panel is never a dead end.
 */
function MarketValuePanel({
  marketValue,
  loading,
  fallbackPriceNok,
  fallbackUpdatedAt,
}: {
  marketValue?: CatalogMarketValue;
  loading?: boolean;
  fallbackPriceNok?: number;
  fallbackUpdatedAt?: string;
}) {
  const { t } = useTranslation();
  const currentPriceNok = marketValue?.currentPriceNok ?? fallbackPriceNok;
  const updatedAt = marketValue?.lastCapturedAt ?? fallbackUpdatedAt;
  const trend = marketValue?.trend;

  return (
    <div className="ui-card grid gap-2 p-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-mist">{t('pricing.marketValue.title')}</span>
        <span className="flex items-center gap-1.5">
          {trend && <Icon name={TREND_ICON[trend].icon} size={13} className={TREND_ICON[trend].className} />}
          <span className="text-parchment">
            {loading ? '…' : currentPriceNok != null ? formatPriceNok(currentPriceNok) : t('pricing.marketValue.none')}
          </span>
        </span>
      </div>

      {currentPriceNok != null && updatedAt && (
        <p className="text-right text-[10px] uppercase tracking-widest text-mist">
          {t('pricing.marketValue.updated', { date: formatDate(updatedAt) })}
        </p>
      )}

      {marketValue && marketValue.series.length > 1 && (
        <Sparkline points={marketValue.series.map((p) => p.priceNok)} />
      )}

      {marketValue && marketValue.crossMarket.length > 0 && (
        <div className="grid gap-1 border-t border-line pt-2">
          <span className="text-[10px] uppercase tracking-widest text-mist">{t('pricing.marketValue.crossMarket')}</span>
          {marketValue.crossMarket.map((row) => (
            <div key={row.catalogId} className="flex items-center justify-between text-xs">
              <span className="capitalize text-mist">{row.source}</span>
              <span className="flex items-center gap-1.5 text-parchment">
                {formatPriceNok(row.priceNok)}
                {row.priceUpdatedAt && <span className="text-[10px] text-mist">({formatDate(row.priceUpdatedAt)})</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Minimal inline SVG sparkline — no chart library. Renders a single polyline scaled to fit min/max. */
function Sparkline({ points, width = 200, height = 32 }: { points: number[]; width?: number; height?: number }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((value, i) => {
    const x = i * step;
    const y = height - ((value - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const rising = points[points.length - 1] >= points[0];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" role="img" aria-hidden="true">
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={rising ? 'var(--ui-color-success, #4ade80)' : 'var(--ui-color-danger, #f87171)'}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * "Antall & plassering" — quantity stepper + location picker + "Venter på
 * levering" switch. Unlike the Prising tab, changes here apply instantly
 * (no draft/Save): a stepper click or a location pick is a single atomic
 * action, same as Vinify's +/- steppers and the cellar table's own steppers.
 * Quantity goes through the regular PATCH (etag-guarded); location/pending
 * go through the dedicated assign-location endpoint so clearing back to
 * "Uplassert" actually clears (see Bottles.assignLocation doc comment).
 */
function QuantityTab({ bottle }: { bottle: Bottle }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['bottle', bottle.id] });
    void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
    void qc.invalidateQueries({ queryKey: ['cellar.stats'] });
    void qc.invalidateQueries({ queryKey: ['locations.tree'] });
  };

  const setQty = useMutation({
    mutationFn: (qty: number) => Bottles.update(bottle.id, { qty }, bottle.etag),
    onSuccess: invalidate,
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const assignLocation = useMutation({
    mutationFn: (payload: { locationId?: string | null; pendingDelivery?: boolean }) =>
      Bottles.assignLocation(bottle.id, payload),
    onSuccess: invalidate,
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  return (
    <div className="grid gap-4">
      <section className="grid gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('pricing.quantity.qty')}</h3>
        <div className="ui-card flex items-center justify-between p-3">
          <span className="text-sm text-mist">{t('pricing.quantity.qty')}</span>
          <div className="flex items-center gap-3">
            <IconButton
              icon="minus"
              label="Reduser antall"
              size="sm"
              disabled={bottle.qty <= 0 || setQty.isPending}
              onClick={() => setQty.mutate(Math.max(0, bottle.qty - 1))}
            />
            <Badge>{`×${bottle.qty}`}</Badge>
            <IconButton
              icon="plus"
              label="Øk antall"
              size="sm"
              disabled={setQty.isPending}
              onClick={() => setQty.mutate(bottle.qty + 1)}
            />
          </div>
        </div>
      </section>

      <div className="ui-divider" />

      <section className="grid gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('pricing.quantity.location')}</h3>
        <LocationPicker
          value={bottle.locationId}
          disabled={assignLocation.isPending}
          unassignedLabel={t('pricing.quantity.noRack')}
          onChange={(locationId) => assignLocation.mutate({ locationId: locationId ?? null })}
        />
        <SwitchRow
          label={t('pricing.quantity.pendingDelivery')}
          description={t('pricing.quantity.pendingDeliveryHint')}
          checked={bottle.pendingDelivery}
          disabled={assignLocation.isPending}
          onChange={(checked) => assignLocation.mutate({ pendingDelivery: checked })}
        />
      </section>
    </div>
  );
}

const EVENT_TONE: Record<CellarEventActivity, 'gold' | 'ruby' | 'mist' | 'success' | 'danger'> = {
  added: 'success',
  drunk: 'mist',
  gifted: 'gold',
  sold: 'ruby',
  adjusted: 'mist',
  moved: 'mist',
  received: 'success',
  counted: 'gold',
};

function HistoryTab({
  events,
  eventsLoading,
  notes,
  loading,
}: {
  events?: CellarEvent[];
  eventsLoading: boolean;
  notes?: Array<{ id: string; rating1to5: number; comment?: string; drunkAt?: string; createdAt: string }>;
  loading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-5">
      <section className="grid gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('pricing.history.events')}</h3>
        {eventsLoading ? (
          <div className="grid place-items-center py-6"><Spinner /></div>
        ) : !events || events.length === 0 ? (
          <p className="py-4 text-center text-sm text-mist">{t('pricing.history.eventsEmpty')}</p>
        ) : (
          <ul className="grid gap-2">
            {events.map((event) => (
              <li key={event.id} className="ui-card grid gap-1 p-3">
                <div className="flex items-center justify-between">
                  <Badge tone={EVENT_TONE[event.activity]}>{t(`history.activity.${event.activity}`)}</Badge>
                  <span className={event.quantityDelta < 0 ? 'text-sm text-danger' : 'text-sm text-parchment'}>
                    {event.quantityDelta > 0 ? `+${event.quantityDelta}` : event.quantityDelta}
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-mist">
                  {formatDate(event.createdAt)} · {t('pricing.history.remaining', { qty: event.remainingAfter })}
                </span>
                {event.note && <p className="text-sm text-parchment">{event.note}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="ui-divider" />

      <section className="grid gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">{t('pricing.history.tastingNotes')}</h3>
        {loading ? (
          <div className="grid place-items-center py-6"><Spinner /></div>
        ) : !notes || notes.length === 0 ? (
          <p className="py-4 text-center text-sm text-mist">{t('pricing.history.empty')}</p>
        ) : (
          <ul className="grid gap-2">
            {notes.map((note) => (
              <li key={note.id} className="ui-card grid gap-1 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-mist">{t('pricing.history.tastingNote')}</span>
                  <Badge tone="gold">{`${note.rating1to5.toFixed(1)} / 5`}</Badge>
                </div>
                {note.comment && <p className="text-sm text-parchment">{note.comment}</p>}
                <span className="text-[10px] uppercase tracking-widest text-mist">
                  {t('pricing.history.drunkAt')}: {formatDate(note.drunkAt ?? note.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function DetailsTab({ bottle }: { bottle: Bottle }) {
  const rows: Array<[string, string | undefined]> = (
    [
      ['Produsent', bottle.producer],
      ['Årgang', bottle.vintage],
      ['Område', bottle.region],
      ['Land', bottle.country],
      ['Druer', bottle.grapeVarieties.join(', ') || undefined],
      ['Strekkode', bottle.barcode],
    ] as Array<[string, string | undefined]>
  ).filter(([, v]) => Boolean(v));

  return (
    <div className="grid gap-2">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between border-b border-line py-2 text-sm last:border-0">
          <span className="text-mist">{label}</span>
          <span className="text-parchment">{value}</span>
        </div>
      ))}
      {bottle.notes && (
        <div className="ui-card mt-2 grid gap-1 p-3">
          <span className="text-xs uppercase tracking-widest text-mist">{'Notater'}</span>
          <p className="text-sm text-parchment">{bottle.notes}</p>
        </div>
      )}
    </div>
  );
}
